/**
 * DocumentsEmailModal
 *
 * Thin wrapper around SingleEmailModal that provides document-specific
 * attachment fetching (direct ADM doc ID download) and customer email logging.
 */

import { useCallback } from 'react';
import { toast } from 'sonner';
import { SingleEmailModal } from '@/components/shared/single-email-modal';
import { useAdmDocumentDownloadAsBase64 } from '@/hooks/useAdmDocumentDownloadAsBase64';
import { useLogCustomerEmail } from '@/hooks/useLogCustomerEmail';
import { type FetchAttachmentsResult, fetchAttachmentsWithProgress } from '@/lib/fetch-attachments-utils';
import { trackError } from '@/lib/telemetry';

// ─── Props ──────────────────────────────────────────────────────────────────

export interface DocumentsEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  accountNumber?: string;
  /** Selected ADM documents to attach (id + file metadata for naming). */
  documents: { id: string; fileName: string; fileType: string }[];
  /** Transaction the documents belong to; links the logged activity to it. */
  transactionId?: number;
  onSuccess?: () => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function DocumentsEmailModal({
  open,
  onOpenChange,
  customerId,
  customerName,
  accountNumber,
  documents,
  transactionId,
  onSuccess,
}: Readonly<DocumentsEmailModalProps>) {
  const { downloadAdmDocumentAsBase64 } = useAdmDocumentDownloadAsBase64();
  const logCustomerEmail = useLogCustomerEmail();

  const handleFetchAttachments = useCallback(
    async (onProgress: (done: number, total: number) => void): Promise<FetchAttachmentsResult> => {
      return fetchAttachmentsWithProgress(
        documents,
        (doc) => downloadAdmDocumentAsBase64(doc.id, doc.fileName, doc.fileType),
        onProgress,
      );
    },
    [documents, downloadAdmDocumentAsBase64],
  );

  const handleAfterSend = useCallback(
    (payload: {
      toEmails: string[];
      ccEmails: string[];
      subject: string;
      htmlBody: string;
      templateId?: string;
      templateName?: string;
      isSubjectModified: boolean;
      isBodyModified: boolean;
      attachOutstandingInvoices: boolean;
    }) => {
      logCustomerEmail.mutate(
        {
          sourceId: customerId,
          toRecipients: payload.toEmails,
          ccRecipients: payload.ccEmails.length > 0 ? payload.ccEmails : undefined,
          subject: payload.subject,
          htmlBody: payload.htmlBody,
          templateId: payload.templateId ? parseInt(payload.templateId, 10) : undefined,
          templateName: payload.templateName,
          isSubjectModified: payload.isSubjectModified,
          isBodyModified: payload.isBodyModified,
          attachOutstandingInvoices: payload.attachOutstandingInvoices,
          transactionIds: transactionId ? [transactionId] : undefined,
        },
        {
          onError: (error) => {
            trackError(error instanceof Error ? error : new Error('Failed to save customer email to history'));
            toast.error('Email was sent but failed to save to history. Please contact support if the issue persists.');
          },
        },
      );
    },
    [logCustomerEmail, customerId, transactionId],
  );

  return (
    <SingleEmailModal
      open={open}
      onOpenChange={onOpenChange}
      customerId={customerId}
      customerName={customerName}
      accountNumber={accountNumber}
      sourceType="transaction"
      callbackEventName="creditcontrol:doc-email-done"
      attachLabel="Attach selected documents"
      initialAttachChecked={true}
      hideAttachCheckbox={true}
      fetchAttachments={handleFetchAttachments}
      onAfterSend={handleAfterSend}
      onSuccess={onSuccess}
      allowedCategoryTypes={['transactions', 'general']}
      allowedTemplateTypes={['email']}
    />
  );
}
